import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { http, setAccessToken, onSessionExpired, refreshSession } from './api.js';

/**
 * MA-5 / FE-18: the access token lives in this module's memory and in nothing
 * else. On a page load there is no token, so the app asks for a fresh one using
 * the HTTP-only refresh cookie the browser holds — which is why a reload keeps
 * you signed in without anything being written to localStorage.
 */
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [merchant, setMerchant] = useState(null);
  // `restoring` is distinct from `loading`: it is the one-time boot check, and
  // routing must wait for it or a reload bounces the user to /login.
  const [restoring, setRestoring] = useState(true);

  const applySession = useCallback((data) => {
    setAccessToken(data.accessToken);
    if (data.user) setUser(data.user);
    if (data.merchant !== undefined) setMerchant(data.merchant);
  }, []);

  const signOut = useCallback(async ({ notifyServer = true } = {}) => {
    if (notifyServer) await http.post('/auth/logout').catch(() => {});
    setAccessToken(null);
    setUser(null);
    setMerchant(null);
  }, []);

  useEffect(() => {
    onSessionExpired(() => { setUser(null); setMerchant(null); });

    let cancelled = false;
    (async () => {
      try {
        const session = await refreshSession();
        if (cancelled) return;
        applySession(session);
        const context = await http.get('/auth/me');
        if (!cancelled) { setUser(context.user); setMerchant(context.merchant); }
      } catch {
        // No valid refresh cookie: a normal signed-out visit, not an error.
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();

    return () => { cancelled = true; };
  }, [applySession]);

  const signIn = useCallback(
    async (credentials) => {
      const data = await http.post('/auth/login', credentials);
      applySession(data);
      return data;
    },
    [applySession],
  );

  const register = useCallback(
    async (details) => {
      const data = await http.post('/auth/register', details);
      applySession(data);
      return data;
    },
    [applySession],
  );

  /** Keeps the sidebar checklist honest after a provider connect or key creation. */
  const refreshMerchant = useCallback(async () => {
    const fresh = await http.get('/dashboard/profile').catch(() => null);
    if (fresh) setMerchant(fresh);
    return fresh;
  }, []);

  const value = useMemo(
    () => ({
      user, merchant, restoring,
      isAuthenticated: Boolean(user),
      isPlatformAdmin: user?.role === 'platform_admin',
      signIn, register, signOut, refreshMerchant, applySession, setMerchant,
    }),
    [user, merchant, restoring, signIn, register, signOut, refreshMerchant, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
